import type { TimelineElement } from '../../context/TimelineContext'
import type { EduPtmDayItem } from '../../services/eduPtmService'
import type { EduLearnerDoc } from '../../services/eduLearnerService'

export function isDayVisibleToStaff(
  day: EduPtmDayItem,
  context: {
    isAdmin: boolean
    currentUserId: string
    elements: TimelineElement[]
  },
): boolean {
  if (context.isAdmin) return true

  const audience = day.audience
  if (!audience) return false

  // Whole school: includes teacher's classes
  if (audience.kind === 'ALL') {
    return true
  }

  // Teacher taught node IDs
  const taughtNodeIds = new Set(
    context.elements
      .filter((el) => {
        const isClass =
          el.edu?.kind === 'CLASS' ||
          el.edu?.kind === 'GROUP' ||
          el.edu?.kind === 'CLIENT'
        return isClass && Boolean(el.edu?.teachers?.[context.currentUserId])
      })
      .map((el) => el.id),
  )

  if (taughtNodeIds.size === 0) return false

  if (audience.kind === 'NODES') {
    return (audience.nodeIds || []).some((nid) => taughtNodeIds.has(nid))
  }

  if (audience.kind === 'ROOT') {
    const rootId = audience.rootId
    if (!rootId) return false
    const elementsById = new Map(context.elements.map((el) => [el.id, el]))
    return Array.from(taughtNodeIds).some((taughtId) => {
      let curr = elementsById.get(taughtId)
      while (curr) {
        if (curr.id === rootId || curr.parentId === rootId) return true
        curr = curr.parentId ? elementsById.get(curr.parentId) : undefined
      }
      return false
    })
  }

  return false
}

export function getTeachersForPtmDayAudience(
  audience: EduPtmDayItem['audience'],
  elements: TimelineElement[],
  directoryMembers?: Array<{ uid: string; displayName?: string; email?: string }>,
): Array<{ uid: string; name: string }> {
  const teacherMap = new Map<string, string>()

  const targetNodeIds = new Set<string>()
  if (audience.kind === 'NODES' && Array.isArray(audience.nodeIds)) {
    audience.nodeIds.forEach((id) => targetNodeIds.add(id))
  } else if (audience.kind === 'ROOT' && audience.rootId) {
    targetNodeIds.add(audience.rootId)
  }

  // Find all classes/groups
  const classOrGroupElements = elements.filter(
    (el) =>
      el.edu?.kind === 'CLASS' ||
      el.edu?.kind === 'GROUP' ||
      el.edu?.kind === 'CLIENT',
  )

  const elementsById = new Map(elements.map((el) => [el.id, el]))

  for (const el of classOrGroupElements) {
    let inAudience = false
    if (audience.kind === 'ALL') {
      inAudience = true
    } else if (audience.kind === 'NODES') {
      inAudience = targetNodeIds.has(el.id)
    } else if (audience.kind === 'ROOT' && audience.rootId) {
      let curr: TimelineElement | undefined = el
      while (curr) {
        if (curr.id === audience.rootId || curr.parentId === audience.rootId) {
          inAudience = true
          break
        }
        curr = curr.parentId ? elementsById.get(curr.parentId) : undefined
      }
    }

    if (inAudience && el.edu?.teachers) {
      for (const [uid, teacherInfo] of Object.entries(el.edu.teachers)) {
        if (!teacherMap.has(uid)) {
          const member = directoryMembers?.find((m) => m.uid === uid)
          const name =
            member?.displayName ||
            member?.email ||
            (typeof teacherInfo === 'object' && teacherInfo !== null && 'name' in teacherInfo
              ? String((teacherInfo as { name?: string }).name || uid)
              : uid)
          teacherMap.set(uid, name)
        }
      }
    }
  }

  return Array.from(teacherMap.entries()).map(([uid, name]) => ({ uid, name }))
}

export function filterLearnersForAudience(
  learners: EduLearnerDoc[],
  audience: EduPtmDayItem['audience'],
  elements: TimelineElement[],
): EduLearnerDoc[] {
  if (audience.kind === 'ALL') {
    return learners.filter((l) => l.status === 'ACTIVE' && !l.frozen)
  }

  const targetNodeIds = new Set<string>()
  if (audience.kind === 'NODES' && Array.isArray(audience.nodeIds)) {
    audience.nodeIds.forEach((id) => targetNodeIds.add(id))
  } else if (audience.kind === 'ROOT' && audience.rootId) {
    targetNodeIds.add(audience.rootId)
    // Add all descendant classes
    const elementsById = new Map(elements.map((el) => [el.id, el]))
    for (const el of elements) {
      let curr: TimelineElement | undefined = el
      while (curr) {
        if (curr.parentId === audience.rootId || curr.id === audience.rootId) {
          targetNodeIds.add(el.id)
          break
        }
        curr = curr.parentId ? elementsById.get(curr.parentId) : undefined
      }
    }
  }

  return learners.filter((l) => {
    if (l.status !== 'ACTIVE' || l.frozen) return false
    return (l.currentNodeIds || []).some((nid) => targetNodeIds.has(nid))
  })
}
